import { useSyncExternalStore } from 'react'
import { writeSetting } from './writeSetting'

/**
 * The curtain over money, on the browser's side of the line.
 *
 * The server renders `data-money="covered"` onto <html> (see
 * ApplicationHelper#html_settings) and lib/themeSync.ts carries it across Turbo
 * visits, exactly as it does the theme and the date style. This reads that one
 * attribute so no screen holds its own copy of the answer.
 *
 * **Why an attribute rather than a prop.** The signed-in app is a dozen
 * separate React roots — one per entrypoint island — plus a good deal of ERB.
 * Drawing the curtain has to change all of them in the same frame, and there is
 * no component above them all to put state in. One attribute on <html> is that
 * place: CSS reads it directly, and the hook below turns it into a re-render.
 *
 * **What it is not.** This is not a security boundary and must never be treated
 * as one. The figures are in the DOM; the curtain is over the reader's eyes,
 * not over the data. Whether somebody may see money at all is
 * `Current#finances_visible?` on the server, which decides what is sent — and
 * that one refuses at the door and cannot be lifted from in here.
 */
export function financesCovered(): boolean {
  return document.documentElement.dataset.money === 'covered'
}

// One observer for the whole page rather than one per hook: every list, card
// and figure subscribes, and an observer each would be a hundred of them
// watching the same attribute.
const listeners = new Set<() => void>()
let observer: MutationObserver | null = null

function subscribe(listener: () => void) {
  listeners.add(listener)

  if (!observer) {
    observer = new MutationObserver(() => listeners.forEach((fire) => fire()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-money'] })
  }

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      observer?.disconnect()
      observer = null
    }
  }
}

/** Re-renders when the curtain moves, wherever it was moved from. */
export function useFinancesCovered(): boolean {
  return useSyncExternalStore(subscribe, financesCovered, () => false)
}

/**
 * Move the curtain now and tell the server afterwards.
 *
 * The attribute goes first and deliberately: the whole feature is for the
 * second somebody sits down beside you, and a curtain that waits for a
 * round-trip has already failed at the only job it has. If the write is
 * refused, `writeSetting` raises its own toast and this puts the attribute
 * back — the one case where the screen briefly led and was wrong.
 */
export async function setFinancesCovered(covered: boolean): Promise<void> {
  const was = financesCovered()
  apply(covered)

  const result = await writeSetting('/finance_cover', { covered })
  if (!result) apply(was)
  else apply(Boolean(result.covered))
}

export function toggleFinancesCovered(): Promise<void> {
  return setFinancesCovered(!financesCovered())
}

function apply(covered: boolean) {
  if (covered) document.documentElement.dataset.money = 'covered'
  else delete document.documentElement.dataset.money
}
